import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Box, Button, FormControl, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { ReportService } from '../../services/report.service';
import { EmployeeConsumptionReport } from '../../interfaces/report.interface';
import { EmployeeAutocomplete } from '../common/EmployeeAutocomplete';
import { helpWhy } from '../../help/why';

/** Local start of the given yyyy-MM-dd day, as an instant. */
const startOfLocalDay = (isoDate: string): Date => {
    const [y, m, d] = isoDate.split('-').map(Number);
    return new Date(y, m - 1, d, 0, 0, 0, 0);
};

/** Last millisecond of the given local day, so the end date is inclusive. */
const endOfLocalDay = (isoDate: string): Date => {
    const [y, m, d] = isoDate.split('-').map(Number);
    return new Date(y, m - 1, d, 23, 59, 59, 999);
};

interface User {
    id: string;
    username: string;
}

const EmployeeConsumption: React.FC = () => {
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [reports, setReports] = useState<EmployeeConsumptionReport[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [generated, setGenerated] = useState<boolean>(false);

    const handleGenerateReport = async () => {
        if (!selectedUser) return;
        try {
            setLoading(true);
            setError('');
            const response = await ReportService.getEmployeeConsumption({
                employeeId: selectedUser.id,
                startDate: startOfLocalDay(startDate).toISOString(),
                endDate: endOfLocalDay(endDate).toISOString()
            });
            setReports(response);
            setGenerated(true);
        } catch (err) {
            setError('Failed to generate report');
            setGenerated(false);
            console.error('Error generating report:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        return format(parseISO(dateString), 'dd-MM-yyyy');
    };

    return (
        <div data-help-page="employeeConsumption.page" style={{ padding: '20px' }}>
            <Typography variant="h4" gutterBottom>
                Employee Consumption Report
            </Typography>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Box data-help-section="employeeConsumption.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, alignItems: 'center' }}>
                    <FormControl sx={{ flex: '1 1 200px', maxWidth: 320 }}>
                        <TextField data-help="employeeConsumption.startDate"
                            label="Start Date"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </FormControl>

                    <FormControl sx={{ flex: '1 1 200px', maxWidth: 320 }}>
                        <TextField data-help="employeeConsumption.endDate"
                            label="End Date"
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </FormControl>

                    <FormControl data-help="employeeConsumption.employee" sx={{ flex: '1 1 200px', maxWidth: 320 }}>
                        <EmployeeAutocomplete<User>
                            label="Select Employee"
                            size="medium"
                            fullWidth
                            value={selectedUser}
                            onChange={setSelectedUser}
                            onError={() => setError('Failed to load users')}
                        />
                    </FormControl>

                    <Button data-help="employeeConsumption.generate"
                        {...helpWhy(!loading && (!selectedUser || !startDate || !endDate) ? { kind: 'status', message: 'Pick an employee, a start date and an end date first.' } : null)}
                        variant="contained"
                        onClick={handleGenerateReport}
                        disabled={!selectedUser || !startDate || !endDate || loading}
                    >
                        Generate Report
                    </Button>
                </Box>

                {loading && <Typography>Loading...</Typography>}

                {error && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#ffebee', color: '#c62828', borderRadius: 1 }}>
                        {error}
                    </Box>
                )}

                {generated && !loading && !error && reports.length === 0 && (
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                        No items were issued to this employee between the selected dates.
                    </Typography>
                )}

                {reports.length > 0 && (
                    <TableContainer data-help-section="employeeConsumption.table">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="employeeConsumption.col.reference">Requisition Reference</TableCell>
                                    <TableCell data-help="employeeConsumption.col.issueDate">Issue Date</TableCell>
                                    <TableCell data-help="employeeConsumption.col.item">Item Name</TableCell>
                                    <TableCell data-help="employeeConsumption.col.category">Category</TableCell>
                                    <TableCell data-help="employeeConsumption.col.units" align="right">Units</TableCell>
                                    <TableCell data-help="employeeConsumption.col.approved">Approved Date</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reports.map((report, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{report.requisitionReference}</TableCell>
                                        <TableCell>{formatDate(report.requisitionIssueDate)}</TableCell>
                                        <TableCell>{report.itemName}</TableCell>
                                        <TableCell>{report.itemCategoryName}</TableCell>
                                        <TableCell align="right">{report.unit}</TableCell>
                                        <TableCell>{formatDate(report.approvedDate)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </div>
    );
};

export default EmployeeConsumption;
